import { ChangeDetectionStrategy, Component, OnInit, inject, OnDestroy, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { FluidModule } from 'primeng/fluid';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabsModule } from 'primeng/tabs';
// Accordion is now built with Tailwind classes (no PrimeNG stepper needed)
import { MenuModule } from 'primeng/menu';
import { BadgeModule } from 'primeng/badge';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { SkeletonModule } from 'primeng/skeleton';
import { RadioButtonModule } from 'primeng/radiobutton';
import { HttpClient } from '@angular/common/http';
import { MessageHelperService } from '../../../base-classes/master-message-service';
import { AuthService } from '../../../auth/login/services/auth.service';
import { ScenarioService } from '../../scenario/services/scenario.service';
import { FuntionalAreaService } from '../../test-suite/services/test-suite.service';
import { ReportGeneratorApiService } from '../services/report-generator-api.service';
import { ReportGeneratorViewModel } from '../viewmodels/report-generator-viewmodel';
import { ClientService } from '../../client/services/client.service';
import { PackagesService } from '../../packages/services/packages.service';

@Component({
    selector: 'app-report-generator',
    imports: [
        InputTextModule, FluidModule, ButtonModule, SelectModule, FormsModule,
        ToolbarModule, TableModule, InputIconModule, IconFieldModule,
        DialogModule, CommonModule, ToastModule, ConfirmDialogModule, ReactiveFormsModule,
        TooltipModule, TagModule, ProgressBarModule, DatePickerModule, ToggleSwitchModule,
        ProgressSpinnerModule, MultiSelect, TabsModule,
        MenuModule, BadgeModule, InputGroupModule, InputGroupAddonModule, SkeletonModule,
        RadioButtonModule
    ],
    templateUrl: './report-generator.component.html',
    styleUrl: './report-generator.component.scss',
    providers: [MessageHelperService, ConfirmationService, MessageService],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportGeneratorComponent implements OnInit, OnDestroy {
    private http = inject(HttpClient);
    private messageService = inject(MessageHelperService);
    private confirmationService = inject(ConfirmationService);
    private authService = inject(AuthService);
    private scenarioService = inject(ScenarioService);
    private functionalAreaService = inject(FuntionalAreaService);
    private reportGeneratorApiService = inject(ReportGeneratorApiService);
    private clientService = inject(ClientService);
    private packagesService = inject(PackagesService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);

    public vm!: ReportGeneratorViewModel;
    public today = new Date();

    private tabSyncEffect = effect(() => {
        if (!this.vm) return;
        const tab = this.vm.activeTab();
        const route = tab === 'list' ? '/report-list' : '/report-generate';
        this.router.navigate([route], { replaceUrl: true });
    });

    ngOnInit(): void {
        this.vm = new ReportGeneratorViewModel(
            this.http,
            this.messageService,
            this.confirmationService,
            this.authService,
            this.scenarioService,
            this.functionalAreaService,
            this.reportGeneratorApiService,
            this.clientService,
            this.packagesService
        );

        const tab = this.route.snapshot.data['tab'];
        if (tab === 'list' || tab === 'generate') {
            this.vm.activeTab.set(tab);
        }
        this.vm.init();

        if (tab === 'generate' && ReportGeneratorViewModel.pendingEditReport) {
            const checkReady = setInterval(() => {
                if (this.vm.clientOptions().length > 0) {
                    clearInterval(checkReady);
                    this.vm.applyPendingEdit();
                }
            }, 100);
            setTimeout(() => clearInterval(checkReady), 5000);
        }
    }

    navigateToTab(tab: 'generate' | 'list'): void {
        this.vm.activeTab.set(tab);
        this.vm.closeViewReport();
        const route = tab === 'list' ? '/report-list' : '/report-generate';
        this.router.navigate([route]);
    }

    screenshotDialogVisible = false;
    screenshotDialogUrl = '';

    openScreenshot(url: string): void {
        this.screenshotDialogUrl = url;
        this.screenshotDialogVisible = true;
    }

    ngOnDestroy(): void {
        this.vm?.destroy();
    }
}
